import { useState } from 'react';
import { BLOBS_BASE_URL } from './constants';
import { bookList } from './utils';

const COVERS_COLLAGE_URL = `${BLOBS_BASE_URL}/bites/covers-collage.jpg`;
const COVERS_COLLAGE_COUNT = bookList.length;

const coversCollagePreload = new Image();
coversCollagePreload.src = COVERS_COLLAGE_URL;

const bitesCollagePreloads = new Map();

function preloadBitesCollage(url) {
  if (bitesCollagePreloads.has(url)) return;
  const img = new Image();
  img.src = url;
  bitesCollagePreloads.set(url, img);
}

export function preloadBookBitesCollage(bookName) {
  if (!bookName) return;
  preloadBitesCollage(`${BLOBS_BASE_URL}/bites/${bookName}/bites-collage.jpg`);
}

function getBiteCount(book) {
  if (!book?.bites?.length) return 0;
  return book.bites.reduce((sum, count) => sum + count, 0);
}

export const ImageO = ({ book, imagePath, bookIndex, className, showCollage = true, showThumbnail = true, showMid = false, showHigh = false, priority = false }) => {
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [midLoaded, setMidLoaded] = useState(false);
  const [highLoaded, setHighLoaded] = useState(false);

  const pathThumbnail = imagePath.replace('.jpg', '-thumbnail.jpg');
  const pathMid = imagePath.replace('.jpg', '-mid.jpg');
  const base = `${BLOBS_BASE_URL}/bites/${book.name}`;

  const biteMatch = imagePath.match(/^bite-(\d+)\.jpg$/);
  const biteIndex = biteMatch ? Number(biteMatch[1]) - 1 : -1;
  const biteCount = biteMatch ? getBiteCount(book) : 0;
  const isBiteCollage = !!biteMatch && biteCount > 0 && biteIndex >= 0;
  const collageUrl = isBiteCollage ? `${base}/bites-collage.jpg` : COVERS_COLLAGE_URL;
  const collageCount = isBiteCollage ? biteCount : COVERS_COLLAGE_COUNT;
  const collageIndex = isBiteCollage ? biteIndex : bookIndex;

  if (isBiteCollage) {
    preloadBitesCollage(collageUrl);
  }

  const canShowCollage = showCollage && typeof collageIndex === 'number' && collageIndex >= 0 && collageCount > 0;
  const showCollageLayer = canShowCollage && !thumbnailLoaded && !midLoaded && !highLoaded;
  const collagePositionPct = showCollageLayer ? (collageIndex / Math.max(collageCount - 1, 1)) * 100 : 0;
  const collageSizePct = collageCount * 100;

  const setLoadedCallback = (showMid, showThumbnail) => {
    if (showMid) {
      setMidLoaded(true);
    } else if (showThumbnail) {
      setThumbnailLoaded(true);
    }
  };

  const pending = !thumbnailLoaded && !midLoaded && !highLoaded && !canShowCollage;

  return (
    <div className={['image-container', pending ? 'image-container-pending' : '', 'always-show', className].filter(Boolean).join(' ')}>
      {showCollageLayer && (
        <div
          className="image-collage"
          style={{
            backgroundImage: `url(${collageUrl})`,
            backgroundSize: `${collageSizePct}% auto`,
            backgroundPositionX: `${collagePositionPct}%`,
          }} />
      )}
      {showThumbnail && !midLoaded && !highLoaded && (
        <img
          className="image-thumbnail"
          src={`${base}/${pathThumbnail}`}
          alt={book.title}
          fetchPriority={priority ? 'high' : undefined}
          loading={priority ? 'eager' : undefined}
          onLoad={() => setLoadedCallback(false, true)}
          onError={() => setLoadedCallback(false, true)} />
      )}
      {(showMid || showHigh) && !highLoaded && (
        <img
          className="image-mid"
          src={`${base}/${pathMid}`}
          alt={book.title}
          onLoad={() => setLoadedCallback(true, false)}
          onError={() => setLoadedCallback(true, false)} />
      )}
      {showHigh && midLoaded && (
        <img
          className="image-high"
          src={`${base}/${imagePath}`}
          alt={book.title}
          onLoad={() => setHighLoaded(true)} />
      )}
    </div>
  );
};
